<template>
  <div id="new-lgb-team-members-modifiy">
    <overlay-loading-progress
      :LoadingFlag="OverlayLoadingFlag"
    ></overlay-loading-progress>
    <!-- Dialogs remain unchanged to preserve functionality -->
    <v-dialog v-model="memberDocumentPrompt" persistent scrollable>
      <upload-selected-member-document
        :record="SelectedMember"
        v-if="memberDocumentPrompt"
        :showDialog="memberDocumentPrompt"
        @hideDialog="hideMemberDocumentPrompt($event)"
      ></upload-selected-member-document>
    </v-dialog>
    <v-dialog v-model="memberPhotoPrompt" persistent scrollable>
      <upload-selected-member-photo
        :record="SelectedMember"
        v-if="memberPhotoPrompt"
        :showDialog="memberPhotoPrompt"
        @hideDialog="hideMemberPhotoPrompt($event)"
      ></upload-selected-member-photo>
    </v-dialog>

    <!-- Main card with enhanced design -->
    <v-card class="main-card overflow-hidden">
      <!-- Header -->
      <div class="premium-header premium-header--dialog">
        <span class="header-orb orb-1"></span>
        <span class="header-orb orb-2"></span>
        <div class="header-inner">
          <div class="header-left">
            <div class="glass-icon glass-icon--sm">
              <v-icon color="white" small>mdi-account-group</v-icon>
            </div>
            <div class="header-text">
              <div class="header-title header-title--sm">
                {{ pageDescription }} {{ NextYearName }}
              </div>
              <div class="header-subtitle">
                Select a designation and member, verify the details and save
              </div>
            </div>
          </div>

          <v-btn icon dark @click="closePrompt" class="transition-swing">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Scrollable content area with enhanced styling -->
      <v-sheet
        id="scrolling-techniques-7"
        class="overflow-y-auto content-container"
        max-height="600"
      >
        <v-container class="py-4">
          <v-form ref="form1" v-model="valid1" lazy-validation id="createForm">
            <!-- Loading spinner and progress indicators -->
            <div class="d-flex justify-content-center mb-4" v-if="LoadingFlag">
              <v-progress-circular
                indeterminate
                color="primary"
                size="50"
              ></v-progress-circular>
            </div>

            <v-container v-if="StartupLoadingFlag" class="px-0">
              <v-row wrap>
                <v-col cols="12" md="4" v-for="n in 6" :key="n">
                  <v-skeleton-loader
                    v-bind="attrs"
                    type="paragraph"
                  ></v-skeleton-loader>
                </v-col>
              </v-row>
            </v-container>

            <v-row wrap v-if="ProgessStart == 1" class="upload-progress">
              <v-col align="center" cols="12" md="12">
                <v-progress-circular
                  :rotate="180"
                  :size="100"
                  :width="15"
                  :value="uploadPercentage"
                  color="#00a4ef"
                  class="mb-3"
                >
                  {{ uploadPercentage }}
                </v-progress-circular>
                <div class="upload-message">
                  Please wait, we are uploading your details.
                  <span class="font-italic"
                    >Time may vary based on your internet connection.</span
                  >
                </div>
              </v-col>
            </v-row>

            <!-- Main form content with beautiful cards -->
            <v-container v-if="!StartupLoadingFlag" class="px-0">
              <!-- Key posts notice -->
              <div class="key-post-notice fade-up delay-1 mb-4">
                <v-icon color="primary" class="mr-3"
                  >mdi-lightbulb-on-outline</v-icon
                >
                <div>
                  <div class="notice-title">
                    Assign key office bearers first
                  </div>
                  <div class="notice-text">
                    Assign the <b>President</b>, <b>Secretary</b> and
                    <b>IPP</b> first. Their names are used in the Nomination
                    committee report.
                  </div>
                </div>
              </div>

              <!-- Required field notice -->
              <div class="required-field-notice fade-up delay-1 mb-5">
                <v-icon color="error" small class="mr-1"
                  >mdi-alert-circle</v-icon
                >
                <span class="text-caption"
                  >Fields marked with
                  <span class="text-error font-weight-bold">*</span> are
                  required</span
                >
              </div>

              <!-- Selection card -->
              <v-card
                class="selection-card fade-up delay-2 mb-5 rounded-lg"
                outlined
              >
                <div class="card-section-header">
                  <span class="section-step">1</span>
                  Select Designation &amp; Member
                </div>
                <v-divider></v-divider>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="field-label">
                        <span class="text-error font-weight-bold mr-1">*</span>
                        Designation
                      </div>
                      <v-autocomplete
                        :reduce="(option) => option.value"
                        :loading="DesignationIdOptionsLoading"
                        :items="DesignationIdOptions"
                        :rules="DesignationIdRules"
                        v-model="DesignationId"
                        required
                        outlined
                        dense
                        clearable
                        hide-details="auto"
                        placeholder="Select designation"
                        class="selection-input"
                        prepend-inner-icon="mdi-shield-account"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="field-label">
                        <span class="text-error font-weight-bold mr-1">*</span>
                        Member
                      </div>
                      <v-autocomplete
                        @change="getMemberDetails"
                        :reduce="(option) => option.value"
                        :loading="MemberIdOptionsLoading"
                        :items="MemberIdOptions"
                        :rules="MemberIdRules"
                        v-model="MemberId"
                        required
                        outlined
                        dense
                        clearable
                        hide-details="auto"
                        placeholder="Select member"
                        class="selection-input"
                        prepend-inner-icon="mdi-account-search"
                      >
                        <template v-slot:selection="{ item }">
                          <v-avatar size="24" class="mr-2">
                            <img :src="item.photo" alt="Member Photo" />
                          </v-avatar>
                          {{ item.text }}
                        </template>
                        <template v-slot:item="{ item }">
                          <v-avatar size="30" class="mr-2">
                            <img :src="item.photo" alt="Member Photo" />
                          </v-avatar>
                          {{ item.text }}
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <v-card
                v-if="MemberFlag"
                class="member-details-card fade-up delay-3 mb-5 rounded-lg"
                outlined
              >
                <div class="card-section-header">
                  <span class="section-step">2</span>
                  Member Details
                  <span class="section-hint"
                    >Profile information and status</span
                  >
                </div>
                <v-divider></v-divider>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="6" md="4">
                      <div class="field-label">
                        <span class="text-error font-weight-bold mr-1">*</span>
                        Date of Birth
                      </div>
                      <v-menu
                        v-model="menu1"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        lazy
                        transition="scale-transition"
                        offset-y
                        full-width
                        min-width="290px"
                        :disabled="!!SelectedMember.DOB"
                      >
                        <template v-slot:activator="{ on }">
                          <v-text-field
                            required
                            v-model="SelectedMember.DOB"
                            label="Date of Birth"
                            persistent-hint
                            readonly
                            v-on="!SelectedMember.DOB ? on : {}"
                            background-color="#F3F6F9"
                            class="date-input"
                            validate-on-blur
                            solo
                            dense
                            :disabled="!!SelectedMember.DOB"
                            prepend-inner-icon="mdi-calendar"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="SelectedMember.DOB"
                          @input="menu1 = false"
                          :disabled="!!SelectedMember.DOB"
                          class="elevation-5"
                        ></v-date-picker>
                      </v-menu>
                      <div class="mt-1">
                        <v-chip
                          x-small
                          :color="
                            SelectedMember.AadhaarVerifiedFlag
                              ? 'success'
                              : 'warning'
                          "
                          text-color="white"
                          class="verification-chip"
                        >
                          <v-icon left x-small>{{
                            SelectedMember.AadhaarVerifiedFlag
                              ? "mdi-check-circle"
                              : "mdi-alert-circle"
                          }}</v-icon>
                          {{
                            SelectedMember.AadhaarVerifiedFlag
                              ? "DOB verified by Aadhaar"
                              : "DOB not verified by Aadhaar"
                          }}
                        </v-chip>
                      </div>
                    </v-col>

                    <!-- Status chips with improved visual design -->
                    <v-col cols="12" sm="6" md="2">
                      <div class="field-label">Profile Update</div>
                      <v-chip
                        :color="SelectedMember.ProfileUpdatedColor"
                        label
                        class="status-chip text-white"
                      >
                        <v-icon left small color="white">
                          {{
                            SelectedMember.ProfileStatusFlag
                              ? "mdi-check-circle"
                              : "mdi-clock-outline"
                          }}
                        </v-icon>
                        {{ SelectedMember.ProfileUpdatedTxt }}
                      </v-chip>
                      <br />
                      <div
                        v-if="SelectedMember.MissingSteps"
                        class="pa-3 rounded-lg text-center"
                        style="
                          background-color: #fff3cd;
                          color: #856404;
                          border: 1px solid #ffeeba;
                        "
                      >
                        <strong>⚠️ Missing Steps:</strong>
                        {{ SelectedMember.MissingSteps }}
                      </div>
                    </v-col>

                    <v-col cols="12" sm="6" md="2">
                      <div class="field-label">
                        <span class="text-error font-weight-bold mr-1">*</span>
                        Photo Update
                      </div>
                      <div class="d-flex align-center">
                        <v-chip
                          :color="
                            SelectedMember.MemberImageFlag
                              ? 'success'
                              : 'warning'
                          "
                          label
                          class="status-chip mr-2"
                        >
                          <v-icon left small>{{
                            SelectedMember.MemberImageFlag
                              ? "mdi-check-circle"
                              : "mdi-clock-outline"
                          }}</v-icon>
                          {{
                            SelectedMember.MemberImageFlag
                              ? "Completed"
                              : "Pending"
                          }}
                        </v-chip>
                      </div>
                    </v-col>

                    <v-col cols="12" sm="6" md="2">
                      <div class="field-label">
                        <span class="text-error font-weight-bold mr-1">*</span>
                        Age Proof Document
                      </div>
                      <div class="d-flex align-center">
                        <v-chip
                          :color="
                            SelectedMember.DocumentFlag ? 'success' : 'warning'
                          "
                          label
                          class="status-chip mr-2"
                        >
                          <v-icon left small>{{
                            SelectedMember.DocumentFlag
                              ? "mdi-check-circle"
                              : "mdi-clock-outline"
                          }}</v-icon>
                          {{
                            SelectedMember.DocumentFlag
                              ? "Completed"
                              : "Pending"
                          }}
                        </v-chip>
                      </div>
                    </v-col>

                    <v-col cols="12" sm="6" md="2">
                      <div class="field-label">
                        <span class="text-error font-weight-bold mr-1">*</span>
                        Aadhaar Status
                      </div>
                      <v-chip
                        :color="SelectedMember.AadhaarTextColor"
                        label
                        class="status-chip text-white"
                      >
                        <v-icon left small>{{
                          SelectedMember.AadhaarVerifiedFlag
                            ? "mdi-check-circle"
                            : "mdi-alert-circle"
                        }}</v-icon>
                        {{ SelectedMember.AadhaarVerifiedTxt }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <v-row class="mb-5 fade-up delay-4" v-if="MemberFlag">
                <!-- Left: Portal Details -->
                <v-col cols="12" md="6">
                  <v-card class="portal-details-card h-100" outlined>
                    <v-card-title class="primary white--text py-2 px-4">
                      <v-icon left color="white">mdi-web</v-icon>
                      Portal Details
                    </v-card-title>
                    <v-card-text class="pa-4">
                      <v-row dense>
                        <v-col cols="12" sm="6">
                          <div class="portal-label">Membership ID</div>
                          <div class="portal-value">
                            {{
                              MembershipId ||
                              SelectedMember.MembershipId ||
                              "NA"
                            }}
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <div class="portal-label">Name</div>
                          <div class="portal-value">
                            {{ SelectedMember.FirstName || FirstName || "NA" }}
                          </div>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <div class="portal-label">Designation</div>
                          <div class="portal-value">
                            {{ SelectedMember.MemberDesignation || "NA" }}
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <div class="portal-label">Contact</div>
                          <div class="portal-value">
                            {{
                              SelectedMember.Mobile ||
                              SelectedMember.MobileNo ||
                              ""
                            }}
                          </div>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <div class="portal-label">Email</div>
                          <div class="portal-value">
                            {{ SelectedMember.EmailId || "NA" }}
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <div class="portal-label">DOB</div>
                          <div class="portal-value">
                            {{ SelectedMember.DobTxt || "NA" }}
                          </div>
                        </v-col>

                        <v-col cols="12">
                          <div class="portal-label">Address</div>
                          <div class="portal-value">
                            {{ SelectedMember.Address || "NA" }}
                          </div>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <div class="portal-label">Profile Update</div>
                          <div class="portal-value">
                            <v-chip
                              small
                              :color="SelectedMember.ProfileUpdatedColor"
                              dark
                              >{{ SelectedMember.ProfileUpdatedTxt }}</v-chip
                            >
                          </div>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <div class="portal-label">Aadhaar Verification</div>
                          <div class="portal-value">
                            <v-chip
                              small
                              :color="SelectedMember.AadhaarTextColor"
                              dark
                              >{{ SelectedMember.AadhaarVerifiedTxt }}</v-chip
                            >
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Right: Aadhaar Card -->
                <v-col cols="12" md="6">
                  <v-card class="aadhaar-card-container h-100" outlined>
                    <v-card-title class="green white--text py-2 px-4">
                      <v-icon left color="white">mdi-account-badge</v-icon>
                      Verified Aadhaar Details
                    </v-card-title>
                    <v-card-text class="pa-0">
                      <template
                        v-if="
                          SelectedMember && SelectedMember.AadhaarVerifiedFlag
                        "
                      >
                        <v-card
                          elevation="0"
                          class="aadhaar-real-card mx-auto"
                          rounded="lg"
                          max-width="100%"
                        >
                          <div class="aadhaar-outer-wrapper">
                            <v-card-text class="pa-4">
                              <div class="d-flex aadhaar-content">
                                <div class="aadhaar-photo-section mr-4">
                                  <v-avatar
                                    size="90"
                                    class="aadhaar-photo-container mb-3"
                                  >
                                    <img
                                      v-if="SelectedMember.PhotoUrl"
                                      :src="SelectedMember.PhotoUrl"
                                      alt="Aadhaar Photo"
                                      class="aadhaar-photo"
                                    />
                                    <v-icon v-else size="50" color="#555555"
                                      >mdi-account</v-icon
                                    >
                                  </v-avatar>
                                </div>
                                <div
                                  class="aadhaar-details-section flex-grow-1"
                                >
                                  <div class="person-name-bilingual">
                                    <div class="name-hindi mb-1">
                                      {{ SelectedMember.AadhaarName || "NA" }}
                                    </div>
                                    <div class="name-english">
                                      {{ SelectedMember.AadhaarName || "NA" }}
                                    </div>
                                  </div>
                                  <div class="detail-row mt-2">
                                    <span class="detail-label"
                                      ><b>Date of Birth:</b></span
                                    >
                                    <span class="detail-value">{{
                                      SelectedMember.AadhaarDob || "NA"
                                    }}</span>
                                  </div>
                                  <div class="detail-row">
                                    <span class="detail-label"
                                      ><b>Gender:</b></span
                                    >
                                    <span class="detail-value">{{
                                      SelectedMember.AadhaarGender || "NA"
                                    }}</span>
                                  </div>
                                  <div>
                                    <span class="detail-label"
                                      ><b>Address:</b></span
                                    >
                                    <span class="detail-value address-text">{{
                                      SelectedMember.AadhaarAddress || "NA"
                                    }}</span>
                                  </div>
                                  <div class="aadhaar-number-container mt-2">
                                    <div class="aadhaar-number-label">
                                      आधार / Aadhaar
                                    </div>
                                    <div class="aadhaar-number">
                                      <template
                                        v-if="SelectedMember.AadhaarNumber"
                                      >
                                        <span class="aadhaar-digit-group">{{
                                          SelectedMember.AadhaarNumber
                                        }}</span>
                                      </template>
                                      <template v-else>XXXX XXXX XXXX</template>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="aadhaar-separator-line mt-3"></div>
                              <div
                                class="aadhaar-footer d-flex justify-space-between align-center mt-1"
                              >
                                <div class="aadhaar-issue-info caption">
                                  <div class="issue-date">
                                    Issued:
                                    {{
                                      SelectedMember.AadhaarIssueDate ||
                                      "01/01/2023"
                                    }}
                                  </div>
                                </div>
                                <div class="aadhaar-tagline">
                                  <span class="tagline-text-hindi"
                                    >आधार - आम आदमी का अधिकार</span
                                  >
                                </div>
                                <div class="verification-badge">
                                  <v-chip
                                    color="success"
                                    x-small
                                    dark
                                    class="verification-chip"
                                    ><v-icon left x-small
                                      >mdi-check-circle</v-icon
                                    >Verified</v-chip
                                  >
                                </div>
                              </div>
                            </v-card-text>
                          </div>
                        </v-card>
                      </template>
                      <template v-else>
                        <v-card-text class="pa-6 aadhaar-empty">
                          <div class="empty-inner">
                            <div class="empty-icon">
                              <v-icon large color="error"
                                >mdi-alert-circle-outline</v-icon
                              >
                            </div>
                            <div class="empty-title">
                              Aadhaar details not found
                            </div>
                            <div class="empty-subtitle caption">
                              No Aadhaar record is available for this member.
                              Please request Aadhaar verification or upload the
                              Aadhaar document.
                            </div>
                          </div>
                        </v-card-text>
                      </template>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
              <!-- Important notice section -->
              <v-card
                class="important-card fade-up delay-4 mb-5 rounded-lg"
                outlined
              >
                <v-card-title class="important-card-title">
                  <v-icon color="amber darken-2" class="mr-2"
                    >mdi-information</v-icon
                  >
                  Important Information
                </v-card-title>
                <v-divider class="mx-4"></v-divider>
                <v-card-text class="py-4">
                  <v-list-item
                    v-for="(item, i) in [
                      'The elected member should have completed their profile updation in their Members Portal.',
                      'The elected member should have their photo uploaded in their profile or you can do it from here by clicking the upload button.',
                      'The elected member should have uploaded their age proof document in their Members Portal or you can do it from here by clicking the upload button.',
                    ]"
                    :key="i"
                    class="px-0"
                  >
                    <v-list-item-icon class="mr-3">
                      <v-avatar
                        size="24"
                        color="amber lighten-4"
                        class="important-list-icon"
                      >
                        <span
                          class="amber--text text--darken-4 font-weight-bold"
                          >{{ i + 1 }}</span
                        >
                      </v-avatar>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="important-list-text">{{
                        item
                      }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-card-text>
              </v-card>
            </v-container>
          </v-form>
        </v-container>
      </v-sheet>

      <!-- Footer action buttons -->
      <v-divider></v-divider>
      <v-card-actions class="pa-4 action-buttons">
        <v-spacer></v-spacer>
        <v-btn
          @click="closePrompt"
          outlined
          color="grey darken-1"
          class="action-btn btn-cancel px-6 mr-3 font-weight-medium"
        >
          <v-icon left>mdi-close-circle</v-icon>
          Cancel
        </v-btn>
        <v-btn
          :disabled="!valid1"
          @click.prevent="confirmAlert"
          :loading="SubmitFlag"
          depressed
          color="success"
          class="action-btn btn-save btn-shine white--text px-6 font-weight-medium"
        >
          <v-icon left>mdi-content-save</v-icon>
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>
<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import UploadSelectedMemberDocument from "@/view/pages/members/UploadSelectedMemberDocument.vue";
import UploadSelectedMemberPhoto from "@/view/pages/members/UploadSelectedMemberPhoto.vue";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {
    UploadSelectedMemberDocument,
    UploadSelectedMemberPhoto,
  },
  props: {
    addRecordPrompt: {
      type: Boolean,
      required: true,
    },
    pageDescription: {
      type: String,
    },
  },
  data() {
    return {
      valid1: true,
      showAadhaarDetails: true,

      menu1: false,
      SelectedMember: {},

      memberDocumentPrompt: false,
      memberPhotoPrompt: false,

      search: "",
      selected: [],
      tableSelectFlag1: false,
      SubmitFlag: false,
      ResultFlag: false,

      rows: [],
      tableColumns1: [],
      records: [],

      alert: {},

      ProgessStart: 0,
      uploadPercentage: 0,

      SuccessMessageTxtFlag: 0,
      SuccessMessageTxt: "",

      ErrorMessageTxtFlag: 0,
      ErrorMessageTxt: "",

      ResultFlag: false,
      LoadingFlag: false,
      StartupLoadingFlag: false,

      AllMembers: [],

      LomCode: "",
      CurrentYearId: "",

      YearCodeRules: [(v) => !!v || "Year is required"],
      YearCode: "",
      YearCodeOptions: [],
      YearCodeOptionsLoading: false,

      TeamCodeRules: [(v) => !!v || "Team is required"],
      TeamCode: "",
      TeamCodeOptions: [],
      TeamCodeOptionsLoading: false,

      ZoneCodeRules: [(v) => !!v || "Zone is required"],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      MembershipId: "",
      MemberFlag: false,
      OverlayLoadingFlag: false,

      MemberIdRules: [(v) => !!v || "Member is required"],
      MemberId: "",
      MemberIdOptions: [],
      MemberIdOptionsLoading: false,

      DesignationIdRules: [(v) => !!v || "Designation is required"],
      DesignationId: "",
      DesignationIdOptions: [],
      DesignationIdOptionsLoading: false,

      MemberNameRules: [(v) => !!v || "Member Name is required"],
      MemberName: "",

      EmailIdRules: [],
      EmailId: "",

      MobileNoRules: [],
      MobileNo: "",

      Address: "",

      CityNameRules: [],
      CityName: "",

      uploadedImages: [],

      FacebookRules: [],
      Facebook: "",

      TwitterRules: [],
      Twitter: "",

      InstagramRules: [],
      Instagram: "",

      MemberTypeId: "",

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = NextJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        this.pageData();
      }
    },
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    rows: function () {
      console.log("watch rows");
      var n1 = this.rows.length;
      var flag = n1 == 0 ? 0 : 1;
      console.log("n1=" + n1 + ", flag=" + flag);
      this.records = this.rows[0];
    },
    YearCodeOptions: function () {
      this.YearCodeOptionsLoading = false;
    },
    TeamCodeOptions: function () {
      this.TeamCodeOptionsLoading = false;
    },
    ZoneCodeOptions: function () {
      this.ZoneCodeOptionsLoading = false;
    },
    MemberIdOptions: function () {
      this.MemberIdOptionsLoading = false;
    },
    DesignationIdOptions: function () {
      this.DesignationIdOptionsLoading = false;
    },
    DesignationId: function () {
      console.log("watch DesignationId");
      var DesignationId = this.DesignationId;
      if (DesignationId != "") {
        var MemberTypeId = "";
        switch (DesignationId) {
          case 14:
            // Junior Jaycee Wing
            MemberTypeId = 9;
            break;

          case 15:
            // Jayceert Wing
            MemberTypeId = 8;
            break;

          default:
            MemberTypeId = 1;
            break;
        }
        this.MemberTypeId = MemberTypeId;
        this.getMemberIdOptions();
      }
    },
  },
  created() {
    // this.initialize()
  },
  methods: {
    hideMemberDocumentPrompt(flag) {
      console.log("hideMemberDocumentPrompt called");
      console.log({ flag });
      this.memberDocumentPrompt = false;
      if (flag) {
        this.SelectedMember.document_flag = true;
      }
    },
    hideMemberPhotoPrompt(flag) {
      console.log("hideMemberPhotoPrompt called");
      console.log({ flag });
      this.memberPhotoPrompt = false;
      if (flag) {
        this.SelectedMember.photo_flag = true;
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "lgb_dis",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getDesignationIdOptions();
    },
    validate() {
      console.log("validate called");
      this.$refs.form1.validate();
    },
    reset() {
      console.log("reset called");
      this.$refs.form1.reset();
    },
    resetValidation() {
      console.log("resetValidation called");
      this.$refs.form1.resetValidation();
    },
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideAddRecordPrompt", this.ResultFlag);
    },
    resetMessageTxt() {
      console.log("resetMessageTxt called");
      this.ErrorMessageTxt = 0;
      this.ErrorMessageTxtFlag = "";
      this.SuccessMessageTxtFlag = 0;
      this.SuccessMessageTxt = "";
    },
    onFileSelected(event, k) {
      console.log("onFileSelected called");
      var n1 = this.uploadedImages.length;
      var file = event.target.files[0];
      console.log("k=" + k + ", event=" + JSON.stringify(file));
      this.uploadedImages[k] = file;
    },
    addNewRow(tableId) {
      console.log("addNewRow called");
      console.log("tableId=" + tableId);

      if (tableId == 2) {
        var n1 = this.AllMembers.length;
        console.log("n1=" + n1);
        // if (n1 < 30) {
        this.AllMembers.push({
          YearwiseOfficesHeldId: null,
          MemberId: 0,
          MemberName: "",
          DesignationId: 0,
          Designation: "",
        });
        // }
      }
    },
    deleteRow(tableId, index, tr) {
      console.log("deleteRow called");
      console.log(
        "tableId=" + tableId + ", index=" + index + ", tr=" + JSON.stringify(tr)
      );
      if (index > -1) {
        if (tableId == 2) {
          this.AllMembers.splice(index, 1);
        }
      }
    },
    getDesignationIdOptions() {
      console.log("getDesignationIdOptions called");
      this.DesignationIdOptionsLoading = true;
      var selectbox1_source = "DesignationId";
      var selectbox1_destination = "DesignationIdOptions";
      var selectbox1_url = "api/designation/options";
      var selectbox1_conditions_array = {
        UserInterface: 2,
        OrganizationTypeId: 3,
        ExceptDesignationId: [1, 5, 6, 7],
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getMemberIdOptions() {
      console.log("getMemberIdOptions called");
      this.MemberIdOptionsLoading = true;
      var selectbox1_source = "MemberId";
      var selectbox1_destination = "MemberIdOptions";
      var selectbox1_url = "api/members/options";
      var selectbox1_conditions_array = {
        UserInterface: 2,
        LomCode: this.LomCode,
        MemberType: this.MemberTypeId,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getMemberDetails() {
      console.log("getMemberDetails called");
      this.AllMembers = [];
      this.SelectedMember = {};
      this.MemberFlag = false;
      var temp_code = this.MemberId;
      console.log("temp_code=" + temp_code);
      var idx = this.MemberIdOptions.map((e) => e.value).indexOf(temp_code);
      console.log("idx=" + idx);
      if (idx >= 0) {
        var member = this.MemberIdOptions[idx];
        console.log("member=" + JSON.stringify(member));
        var MembershipId = member.membership_id;
        MembershipId = MembershipId == (null || undefined) ? "" : MembershipId;
        console.log({ MembershipId });
        if (MembershipId != "") {
          this.MembershipId = MembershipId;
          this.searchForm();
        }
      }
    },
    searchForm() {
      console.log("searchForm is called");
      var MembershipId = this.MembershipId;
      console.log("MembershipId=" + MembershipId);
      this.SelectedMember = {};
      this.MemberFlag = false;
      if (MembershipId != "") {
        this.OverlayLoadingFlag = true;
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members/search";
        var upload = {
          UserInterface: 2,
          MembershipId: this.MembershipId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );
        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;
        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));
            // thisIns.pageLoadingOff()
            thisIns.OverlayLoadingFlag = false;
            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.SelectedMember = records;
              thisIns.MemberFlag = true;
              // thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "Membership Id is required";
        this.sweetAlert("error", message, false);
      }
    },
    confirmAlert() {
      var validate1 = this.$refs.form1.validate();
      var photo_flag = this.SelectedMember.photo_flag;
      var document_flag = this.SelectedMember.document_flag;
      var dob =
        this.SelectedMember.DOB == undefined ? "" : this.SelectedMember.DOB;
      console.log({ validate1 }, { document_flag }, { photo_flag }, { dob });
      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }

        this.sweetAlert("error", message, false);
      }
    },
    submitForm() {
      // add todo in store and clear all fields in dialog
      console.log("submitForm called");

      // this.resetMessageTxt();

      var result = this.$refs.form1.validate();
      console.log("result=" + result);

      var LomCode = this.LomCode;
      var MemberId = this.MemberId;
      var SelectedMember = this.SelectedMember;
      // var n1 = AllMembers.length;

      var CurrentYearId = this.CurrentYearId;
      console.log("CurrentYearId=" + CurrentYearId);

      if (result && CurrentYearId > 0) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url =
          server_url + "api/year-wise-designation/new-governing-board";
        var upload = {
          UserInterface: 1,
          OrganizationTypeId: 3,
          YearCode: CurrentYearId,
          ZoneCode: this.ZoneCode,
          LomCode: this.LomCode,
          MemberId: this.MemberId,
          DesignationId: this.DesignationId,
          DateOfBirth: SelectedMember.DOB,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        this.OverlayLoadingFlag = true;
        this.ResultFlag = false;

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        // thisIns.pageLoadingOn()

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            // thisIns.ProgessStart = 0;
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              // thisIns.SuccessMessageTxtFlag = 1;
              // thisIns.SuccessMessageTxt = output;
              thisIns.ResultFlag = true;
              thisIns.closePrompt();
              // thisIns.AllMembers = [];
              // thisIns.ResultFlag = false;
              thisIns.sweetAlert("success", output, true);
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        console.log("some errors occured");
        var message = "";
        if (LomCode == "") {
          message += "LO should not be empty. ";
        }
        if (MemberId == "") {
          message += "Member should not be empty. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    console.log("beforeMount called");
    var LomCode = this.$session.get("LomId");
    LomCode = LomCode == (null || undefined) ? "" : LomCode;
    console.log("LomCode=" + LomCode);

    var ZoneCode = this.$session.get("ZoneId");
    ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
    console.log("ZoneCode=" + ZoneCode);

    this.LomCode = LomCode;
    this.ZoneCode = ZoneCode;
    this.refreshPageData();
  },
};
</script>

<style lang="scss">
$primary-dark: #0b2a5b;
$primary: #1565c0;
$accent: #00b4d8;
$success: #16a34a;
$warning: #f59e0b;
$surface: #ffffff;
$page-bg: #f4f7fb;
$border: #e3e8ef;
$text-strong: #0f172a;
$text-muted: #64748b;
$shadow-soft: 0 1px 2px rgba(15, 23, 42, 0.06),
  0 8px 24px rgba(15, 23, 42, 0.06);
$shadow-strong: 0 2px 4px rgba(15, 23, 42, 0.08),
  0 16px 32px rgba(15, 23, 42, 0.14);
$header-gradient: linear-gradient(
  135deg,
  $primary-dark 0%,
  $primary 60%,
  $accent 100%
);

@keyframes lgbFadeUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes lgbFadeIn {
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes lgbPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.55);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(245, 158, 11, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
  }
}

@keyframes lgbFloat {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(12px, -10px);
  }
}

#new-lgb-team-members-modifiy {
  .premium-header {
    position: relative;
    overflow: hidden;
    background: $header-gradient;
    color: #ffffff;
    padding: 22px 24px;

    .header-orb {
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.12);
      filter: blur(2px);
      pointer-events: none;
      animation: lgbFloat 9s ease-in-out infinite;
    }

    .orb-1 {
      width: 220px;
      height: 220px;
      top: -110px;
      right: -40px;
    }

    .orb-2 {
      width: 140px;
      height: 140px;
      bottom: -80px;
      right: 22%;
      background: rgba(0, 180, 216, 0.22);
      animation-delay: 1.5s;
    }

    .orb-3 {
      width: 90px;
      height: 90px;
      top: 10px;
      left: 38%;
      background: rgba(255, 255, 255, 0.07);
      animation-delay: 3s;
    }

    .header-inner {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }

    .header-left {
      display: flex;
      align-items: center;
      min-width: 0;
    }

    .glass-icon {
      flex: 0 0 48px;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 14px;
      background: rgba(255, 255, 255, 0.15);
      border: 1px solid rgba(255, 255, 255, 0.25);
      backdrop-filter: blur(8px);
      animation: lgbFadeIn 0.5s ease-out both;

      &.glass-icon--sm {
        flex-basis: 38px;
        width: 38px;
        height: 38px;
      }
    }

    .header-title {
      font-size: 1.4rem;
      font-weight: 700;
      letter-spacing: 0.3px;
      line-height: 1.25;
      margin: 0;
      color: #ffffff;

      &.header-title--sm {
        font-size: 1.1rem;
      }
    }

    .header-subtitle {
      font-size: 0.85rem;
      opacity: 0.85;
      margin-top: 2px;
    }

    .glass-btn {
      height: 40px !important;
      border-radius: 10px !important;
      background: rgba(255, 255, 255, 0.16) !important;
      border: 1px solid rgba(255, 255, 255, 0.35);
      backdrop-filter: blur(8px);
      transition: transform 0.2s, box-shadow 0.2s, background 0.2s;

      &:hover {
        transform: translateY(-2px);
        background: rgba(255, 255, 255, 0.26) !important;
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
      }
    }

    &.premium-header--dialog {
      padding: 16px 20px;
      position: sticky;
      top: 0;
      z-index: 5;
    }
  }

  .fade-up {
    animation: lgbFadeUp 0.4s ease-out both;

    &.delay-1 {
      animation-delay: 0ms;
    }
    &.delay-2 {
      animation-delay: 80ms;
    }
    &.delay-3 {
      animation-delay: 160ms;
    }
    &.delay-4 {
      animation-delay: 240ms;
    }
  }

  .h-100 {
    height: 100%;
  }

  .btn-shine {
    position: relative;
    overflow: hidden;

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: -75%;
      width: 50%;
      height: 100%;
      background: linear-gradient(
        120deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.45) 50%,
        rgba(255, 255, 255, 0) 100%
      );
      transform: skewX(-20deg);
      transition: left 0.6s ease;
    }

    &:hover::after {
      left: 125%;
    }
  }

  .btn-save {
    background: linear-gradient(
      135deg,
      #15803d 0%,
      $success 60%,
      #22c55e 100%
    ) !important;
  }

  .v-dialog > .v-card > .v-toolbar {
    position: sticky;
    top: 0;
    z-index: 999;
  }

  // Main container and layout styles
  // Card fits inside the dialog so only the content area scrolls
  .main-card {
    border-radius: 14px !important;
    overflow: hidden;
    box-shadow: $shadow-strong !important;
    display: flex;
    flex-direction: column;
    max-height: calc(90vh - 4px);
  }

  .premium-header,
  .action-buttons {
    flex: 0 0 auto;
  }

  .content-container {
    background-color: $page-bg;
    flex: 1 1 auto;
    min-height: 0;
    max-height: none !important;
  }

  .key-post-notice {
    display: flex;
    align-items: flex-start;
    background: linear-gradient(
      90deg,
      rgba(21, 101, 192, 0.08) 0%,
      #ffffff 100%
    );
    border: 1px solid rgba(21, 101, 192, 0.18);
    border-left: 4px solid $primary;
    border-radius: 12px;
    padding: 14px 16px;

    .notice-title {
      font-size: 14px;
      font-weight: 700;
      color: $primary-dark;
    }

    .notice-text {
      font-size: 13px;
      color: $text-muted;
    }
  }

  .required-field-notice {
    display: flex;
    align-items: center;
    background-color: $surface;
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px solid $border;
    border-left: 4px solid #f44336;
  }

  // Card styling
  .selection-card,
  .member-details-card,
  .aadhaar-card,
  .important-card,
  .portal-details-card,
  .aadhaar-card-container {
    border-radius: 14px !important;
    border-color: $border !important;
    overflow: hidden;
    box-shadow: $shadow-soft !important;
    transition: box-shadow 0.2s, transform 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: $shadow-strong !important;
    }
  }

  .card-section-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 14px 18px;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.2px;
    color: $text-strong;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);

    .section-step {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: linear-gradient(135deg, $primary 0%, $accent 100%);
      box-shadow: 0 4px 10px rgba(21, 101, 192, 0.3);
      color: #ffffff;
      font-size: 13px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-right: 10px;
    }

    .section-hint {
      margin-left: 10px;
      font-size: 12px;
      font-weight: 400;
      color: $text-muted;
    }
  }

  // Form input styling
  .field-label {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
    color: $text-strong;
  }

  .selection-input,
  .date-input {
    border-radius: 10px;
  }

  // Status chips styling
  .status-chip {
    min-width: 100px;
    border-radius: 8px !important;
    transition: transform 0.25s ease, opacity 0.25s ease;
  }

  .verification-chip {
    height: 24px;
  }

  // Important notice styling
  .important-card-title {
    color: #b45309;
    font-size: 15px;
    font-weight: 700;
  }

  .important-list-text {
    font-size: 13px;
    line-height: 1.6;
    white-space: normal;
    color: $text-muted;
  }

  // Sticky footer
  .action-buttons {
    position: sticky;
    bottom: 0;
    z-index: 5;
    background-color: $surface;
    border-top: 1px solid $border;
  }

  .action-btn {
    height: 40px !important;
    border-radius: 10px !important;
    text-transform: none;
    font-size: 14px;
    letter-spacing: 0.3px;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(15, 23, 42, 0.18) !important;
    }
  }

  .portal-details-card .v-card__title,
  .aadhaar-card-container .v-card__title {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.2px;
  }

  .portal-details-card .v-card__title.primary {
    background: linear-gradient(
      135deg,
      $primary-dark 0%,
      $primary 100%
    ) !important;
  }

  .aadhaar-card-container .v-card__title.green {
    background: linear-gradient(135deg, #15803d 0%, $success 100%) !important;
  }

  // Upload progress styling
  .upload-progress {
    background-color: rgba(0, 164, 239, 0.05);
    border-radius: 12px;
    padding: 24px;
    border: 1px dashed #00a4ef;
  }

  .upload-message {
    color: #f44336;
    font-weight: 500;
  }

  // Text colors
  .text-error {
    color: #f44336 !important;
  }

  /* Aadhaar Card Styles - Keeping original functionality */
  .aadhaar-real-card {
    background: white !important;
    overflow: hidden;
    border: 1px solid #e0e0e0;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.02);
    }

    .aadhaar-header {
      background-color: #f8f8f8;
      padding: 8px;
    }

    .aadhaar-title-container .govt-title-hindi {
      color: black !important;
      font-size: 14px !important;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }

    .aadhaar-title-container .govt-title-english {
      color: #333 !important;
      font-size: 12px !important;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }

    .govt-title-hindi {
      font-size: 14px;
      font-weight: bold;
      color: #000 !important;
      margin: 0;
      line-height: 1.2;
    }

    .govt-title-english {
      font-size: 12px;
      font-weight: 600;
      color: #333 !important;
      margin: 0;
      line-height: 1.2;
    }

    .aadhaar-flag-colors {
      display: flex;
      flex-direction: column;
      height: 6px;
      width: 100%;

      .saffron-line {
        height: 2px;
        background: #ff9933;
      }

      .white-line {
        height: 2px;
        background: #ffffff;
        position: relative;

        .ashoka-chakra {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }

      .green-line {
        height: 2px;
        background: #138808;
      }
    }

    .aadhaar-logo {
      font-size: 14px;
    }

    .aadhaar-content {
      display: flex;
      align-items: flex-start;
    }

    .aadhaar-photo-container {
      border: 1px solid #ccc;
      background: #f5f5f5;
    }

    .qr-code-container {
      border: 1px dashed #ccc;
      padding: 2px;
      background: #f9f9f9;
      width: fit-content;
    }

    .aadhaar-details-section {
      flex: 1;
    }

    .person-name-bilingual {
      .name-hindi {
        font-size: 12px;
        color: #555;
      }

      .name-english {
        font-size: 14px;
        font-weight: bold;
        color: #333;
      }
    }

    .detail-row {
      margin-bottom: 4px;

      .detail-label {
        display: inline-block;
        min-width: 90px;
        font-weight: 600;
        font-size: 12px;
        color: #555;
      }

      .detail-value {
        font-size: 12px;
        color: #333;
      }

      .address-text {
        max-width: 200px;
        display: inline-block;
        word-break: break-word;
      }
    }

    .aadhaar-number-container {
      background: #f5f5f5;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 4px 8px;
      display: inline-block;
      max-width: 200px;

      .aadhaar-number-label {
        font-size: 9px;
        color: #666;
        margin-bottom: 2px;
      }

      .aadhaar-number {
        font-weight: bold;
        letter-spacing: 1px;
        color: #000;
        font-size: 14px;
        font-family: "Courier New", monospace;
      }
    }

    .aadhaar-separator-line {
      height: 2px;
      background: linear-gradient(
        to right,
        #f00,
        #f00 75%,
        transparent 75%,
        transparent 100%
      );
      background-size: 10px 1px;
      background-repeat: repeat-x;
    }

    .aadhaar-footer {
      .issue-date {
        font-size: 8px;
        color: #666;
      }

      .tagline-text-hindi {
        font-size: 10px;
        color: #d32f2f;
        font-weight: 500;
      }
    }
  }
  .portal-details-card {
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
    .portal-label {
      font-size: 12px;
      color: #37474f;
      font-weight: 600;
      margin-bottom: 4px;
    }
    .portal-value {
      font-size: 14px;
      color: #1f2937;
      margin-bottom: 10px;
    }
  }

  .aadhaar-card-container {
    border-radius: 8px;
    overflow: hidden;
    .aadhaar-empty {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 28px !important;
      .empty-inner {
        max-width: 420px;
        text-align: center;
      }
      .empty-icon {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 12px;
        background: rgba(244, 67, 54, 0.08);
      }
      .empty-title {
        font-size: 18px;
        font-weight: 700;
        color: #263238;
        margin-bottom: 6px;
      }
      .empty-subtitle {
        color: #6b7280;
        font-size: 13px;
        line-height: 1.45;
        margin-bottom: 12px;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
      transition: none !important;
    }
  }
}
</style>